import { PhotoTrigger } from '../photo-viewer.tsx';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fileApi, type QuoteFile } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { Alert, Button, EmptyState, Spinner } from '../ui.tsx';
import { ALLOWED_ATTACHMENT_TYPES, ALLOWED_IMAGE_TYPES } from '@shared/constants/index.ts';

/**
 * Quote attachments and photos (spec §14).
 *
 * The Worker has always been able to store these; nothing in the app could
 * reach it, so files uploaded through the API were invisible and no user could
 * add one. Downloads go through the Worker — R2 is private and never fetched
 * directly by the browser — so the link here is an ordinary same-origin URL
 * that carries the session cookie.
 */

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(file: QuoteFile): boolean {
  return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(file.contentType);
}

interface AttachmentsProps {
  quoteId: string;
  readOnly: boolean;
}

export default function Attachments({ quoteId, readOnly }: AttachmentsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const files = useQuery({
    queryKey: ['quote-files', quoteId],
    queryFn: () => fileApi.listForQuote(quoteId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['quote-files', quoteId] });

  const upload = useMutation({
    mutationFn: (file: File) =>
      // Photos are pictures of the property; anything else (a floor plan, a
      // signed scope) is an attachment. The server enforces the type list for
      // each by magic number, so this only decides which list applies.
      fileApi.upload(quoteId, file, isImageType(file.type) ? 'quote_photo' : 'attachment'),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (caught) => {
      setError(caught instanceof ApiRequestError ? caught.message : t('attachments.uploadError'));
    },
    onSettled: () => {
      // Clearing the input means re-picking the same file fires `change` again.
      if (inputRef.current) inputRef.current.value = '';
    },
  });

  const remove = useMutation({
    mutationFn: (fileId: string) => fileApi.remove(fileId),
    onSuccess: () => void invalidate(),
    onError: () => setError(t('attachments.removeError')),
  });

  const usage = files.data?.usage;
  // Unlimited plans report null caps, so the count comparison is skipped
  // entirely rather than compared against a missing number.
  const atLimit = usage && !usage.unlimited && usage.maxCount !== null ? usage.count >= usage.maxCount : false;

  return (
    <section className="card" aria-labelledby="attachments-heading">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 id="attachments-heading" className="text-lg font-semibold">
          {t('attachments.heading')}
        </h2>
        {usage ? (
          <p className="text-sm text-[color:var(--color-muted)]">
            {usage.unlimited || usage.maxCount === null || usage.maxTotalBytes === null
              ? t('attachments.usageUnlimited', {
                  count: usage.count,
                  used: formatBytes(usage.totalBytes),
                })
              : t('attachments.usage', {
                  count: usage.count,
                  max: usage.maxCount,
                  used: formatBytes(usage.totalBytes),
                  limit: formatBytes(usage.maxTotalBytes),
                })}
          </p>
        ) : null}
      </div>

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {!readOnly ? (
        <div className="mb-4">
          <label className="field-label" htmlFor="attachment-upload">
            {t('attachments.addFile')}
          </label>
          <p className="field-hint mb-2">{t('attachments.addFileHint')}</p>
          <input
            ref={inputRef}
            id="attachment-upload"
            type="file"
            accept={ALLOWED_ATTACHMENT_TYPES.join(',')}
            disabled={upload.isPending || atLimit}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) upload.mutate(file);
            }}
          />
          {atLimit ? <p className="field-hint mt-2">{t('attachments.atLimit')}</p> : null}
          {upload.isPending ? (
            <p className="mt-2 flex items-center gap-2 text-sm">
              <Spinner /> {t('attachments.uploading')}
            </p>
          ) : null}
        </div>
      ) : null}

      {files.isPending ? (
        <p className="flex items-center gap-2 text-sm">
          <Spinner /> {t('attachments.loadingFiles')}
        </p>
      ) : files.data && files.data.items.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-2">
          {files.data.items.map((file) => (
            <li key={file.id} className="flex items-center gap-3 rounded border border-[color:var(--color-line)] p-3">
              {isImage(file) ? (
                <PhotoTrigger
                  src={file.downloadUrl}
                  alt={file.originalFilename ?? t('attachments.untitledFile')}
                  group={`quote-${quoteId}`}
                  filename={file.originalFilename ?? undefined}
                >
                  <img
                    src={file.downloadUrl}
                    alt={file.originalFilename ?? t('attachments.untitledFile')}
                    className="h-12 w-12 shrink-0 rounded object-cover"
                    loading="lazy"
                  />
                </PhotoTrigger>
              ) : (
                <span
                  aria-hidden="true"
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-[color:var(--color-surface)] text-xs font-semibold"
                >
                  PDF
                </span>
              )}

              <div className="min-w-0 flex-1">
                <a href={file.downloadUrl} className="block truncate font-medium underline">
                  {file.originalFilename ?? t('attachments.untitledFile')}
                </a>
                <p className="text-sm text-[color:var(--color-muted)]">{formatBytes(file.sizeBytes)}</p>
              </div>

              {!readOnly ? (
                <Button
                  variant="ghost"
                  busy={remove.isPending && remove.variables === file.id}
                  onClick={() => {
                    const name = file.originalFilename ?? t('attachments.untitledFile');
                    if (window.confirm(t('attachments.removeConfirm', { name }))) remove.mutate(file.id);
                  }}
                >
                  <span className="sr-only">
                    {t('attachments.removeNamed', { name: file.originalFilename ?? t('attachments.untitledFile') })}
                  </span>
                  <span aria-hidden="true">{t('common.remove')}</span>
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title={t('attachments.emptyTitle')}>
          {readOnly ? t('attachments.emptyReadOnly') : t('attachments.emptyDefault')}
        </EmptyState>
      )}
    </section>
  );
}

function isImageType(contentType: string): boolean {
  return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(contentType);
}
